import ArticleIcon from "@mui/icons-material/Article";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";

// File type arrays (shared constants)
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/bmp", "image/webp", "image/svg+xml"];
// CSV is categorized as TEXT_TYPES (not EXCEL_TYPES) because it's a plain text format
// that can be previewed directly in the browser without special formatting, unlike binary Excel files
export const TEXT_TYPES = ["text/plain", "text/csv", "text/log"];
export const PDF_TYPES = ["application/pdf"];
export const WORD_TYPES = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
export const EXCEL_TYPES = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet"
];
export const POWERPOINT_TYPES = ["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"];
export const DOCUMENT_TYPES = [...PDF_TYPES, ...WORD_TYPES, ...EXCEL_TYPES, ...POWERPOINT_TYPES];

// Map file extensions to MIME types for fallback validation
export const EXTENSION_TO_MIME_MAP: Record<string, string[]> = {
    ".pdf": PDF_TYPES,
    ".doc": [WORD_TYPES[0]],
    ".docx": [WORD_TYPES[1]],
    ".xls": [EXCEL_TYPES[0]],
    ".xlsx": [EXCEL_TYPES[1]],
    ".ods": [EXCEL_TYPES[2]],
    ".ppt": [POWERPOINT_TYPES[0]],
    ".pptx": [POWERPOINT_TYPES[1]],
    ".csv": ["text/csv"],
    ".log": ["text/log"],
    ".txt": ["text/plain"],
    ".png": ["image/png"],
    ".jpg": ["image/jpeg"],
    ".jpeg": ["image/jpeg"],
    ".gif": ["image/gif"],
    ".bmp": ["image/bmp"],
    ".webp": ["image/webp"],
    ".svg": ["image/svg+xml"]
};

export const DEFAULT_ACCEPTED_UPLOAD_FILE_TYPES = Object.keys(EXTENSION_TO_MIME_MAP);

const normalizeAcceptedUploadType = (fileType: string): string => {
    const normalizedType = fileType.trim().toLowerCase();
    if (!normalizedType) return "";
    return normalizedType.startsWith(".") ? normalizedType : `.${normalizedType}`;
};

/**
 * Resolves accepted upload file types from app configuration with defaults.
 * `accepted_upload_file_types` is expected as string[] from server.
 */
export function resolveAcceptedUploadFileTypes(configuredTypes?: string[]): string[] {
    if (!Array.isArray(configuredTypes) || configuredTypes.length === 0) {
        return [...DEFAULT_ACCEPTED_UPLOAD_FILE_TYPES];
    }

    const normalizedTypes = Array.from(new Set(configuredTypes.map(type => normalizeAcceptedUploadType(type)).filter(Boolean)));
    return normalizedTypes.length > 0 ? normalizedTypes : [...DEFAULT_ACCEPTED_UPLOAD_FILE_TYPES];
}

/**
 * Validates if a file is accepted based on MIME type and extension fallback
 * This provides more reliable validation as some browsers may not set correct MIME types
 * @param file - The File object to validate
 * @param acceptedUploadFileTypes - Normalized extension list (for raw config, use resolveAcceptedUploadFileTypes first)
 * @returns true if the file type is accepted, false otherwise
 */
export function isFileTypeAccepted(file: File, acceptedUploadFileTypes: string[] = DEFAULT_ACCEPTED_UPLOAD_FILE_TYPES): boolean {
    const allowedExtensions = acceptedUploadFileTypes;

    const fileName = file.name.toLowerCase();
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = extensionIndex >= 0 ? fileName.substring(extensionIndex) : "";

    // First check extension against configured allowed types
    if (extension && allowedExtensions.includes(extension)) {
        return true;
    }

    // Fallback: check MIME type against configured extensions
    if (file.type) {
        return allowedExtensions.some(allowedExtension => (EXTENSION_TO_MIME_MAP[allowedExtension] ?? []).includes(file.type));
    }

    return false;
}

/**
 * Returns the appropriate icon component for a given MIME type
 * @param mime - The MIME type of the file
 * @param size - Optional size for the icon (default varies by usage)
 * @returns React icon component
 */
export function getFileIconByMimeType(mime: string, size?: "small" | "medium" | "large" | number) {
    const sx = typeof size === "number" ? { fontSize: size } : undefined;

    if (IMAGE_TYPES.includes(mime) || mime.startsWith("image/")) {
        return <ImageIcon color="primary" sx={sx} />;
    }
    if (PDF_TYPES.includes(mime)) {
        return <PictureAsPdfIcon color="error" sx={sx} />;
    }
    if (WORD_TYPES.includes(mime)) {
        return <ArticleIcon color="primary" sx={sx} />;
    }
    if (EXCEL_TYPES.includes(mime)) {
        return <TableChartIcon color="success" sx={sx} />;
    }
    if (POWERPOINT_TYPES.includes(mime)) {
        return <SlideshowIcon color="warning" sx={sx} />;
    }
    if (TEXT_TYPES.includes(mime)) {
        return <TextSnippetIcon color="info" sx={sx} />;
    }
    return <InsertDriveFileIcon color="primary" sx={sx} />;
}

/**
 * Converts file size in bytes to human-readable format
 * @param size - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export function getReadableFileSize(size: number): string {
    if (size < 1024) return size + " B";
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
    return (size / (1024 * 1024)).toFixed(2) + " MB";
}

/**
 * Gets the MIME type for a file with fallback to extension-based detection
 * This is needed because browsers don't always provide MIME types for certain file extensions
 * @param file - The File object
 * @returns The MIME type string
 */
export function getFileType(file: { type?: string; name?: string }): string {
    let fileType = file?.type ?? "";

    // Fallback: If browser doesn't provide MIME type, determine from file extension
    if (!fileType || fileType === "" || fileType === "application/octet-stream") {
        const fileName = (file?.name ?? "").toLowerCase();
        const extension = fileName.includes(".") ? fileName.substring(fileName.lastIndexOf(".")) : "";

        // Reuse the existing EXTENSION_TO_MIME_MAP and get the first MIME type from the array
        const mimeTypes = EXTENSION_TO_MIME_MAP[extension];
        if (mimeTypes && mimeTypes.length > 0) {
            fileType = mimeTypes[0];
        }
    }

    return fileType;
}
